import Icon from '@expo/vector-icons/MaterialCommunityIcons';
import {
  FeatureName,
  useCurrentAccount,
  useCurrentWorkspace,
  useFeatures,
  useIsFeatureEnabled,
  WorkspaceType,
} from '@gripp/shared-logic';
import { Colors } from '@gripp/shared-ui';
import { useEffect } from 'react';
import { StyleSheet, View } from 'react-native';
import { PaperProvider, Text } from 'react-native-paper';
import { useNavigate } from 'react-router-dom';

export const Home = () => {
  const navigate = useNavigate();

  // Landing decision (OEM Home Dashboard, PR 1b): flag-on OEM lands on /home;
  // every other workspace type and every flag-off case lands on /activities.
  // To avoid a flash/bounce, keep showing the splash below until the account
  // and features have settled, then decide exactly once.
  const workspace = useCurrentWorkspace();
  const { account, isFetching: accountFetching } = useCurrentAccount();
  const { data: features, isLoading: featuresLoading } = useFeatures();
  const isHomeDashboardEnabled = useIsFeatureEnabled(
    FeatureName.OemHomeDashboard
  );
  // The dashboard is built entirely in Crop Circle (glass surfaces, tokenized
  // chrome) and assumes the CC skin — without it the page renders under the
  // legacy Header and reads as broken. Gate landing on CC too, so the landing
  // decision, the page self-guard, and the nav reorder all agree on the same
  // three conditions (CC + OEM Home flag + OEM workspace).
  const isCropCircleEnabled = useIsFeatureEnabled(
    FeatureName.CropCircleRedesign
  );

  // Wait only while the account or features are GENUINELY still loading — not
  // on a workspace that is legitimately absent. useCurrentWorkspace() returns
  // null/undefined both while fetching AND for accounts with no resolved
  // workspace (a global admin who hasn't selected/impersonated one, lonely
  // larry). Gating on `workspace` being truthy stranded those users on this
  // orange splash forever; instead settle on the account, then treat a falsy
  // workspace as "not OEM" and fall through to /activities.
  const accountResolving = !account && accountFetching;
  const featuresResolving = featuresLoading || !features;
  const isResolving = accountResolving || featuresResolving;

  useEffect(() => {
    if (isResolving) return;

    const isOem = workspace?.type === WorkspaceType.Oem;
    const landOnHome = isOem && isHomeDashboardEnabled && isCropCircleEnabled;
    navigate(landOnHome ? '/home' : '/activities', {
      replace: true,
    });
  }, [
    isResolving,
    workspace,
    isHomeDashboardEnabled,
    isCropCircleEnabled,
    navigate,
  ]);

  return (
    <PaperProvider>
      <View style={styles.container}>
        <Icon name="qrcode-scan" style={styles.icon} />
        <Text style={styles.text}>Gripp Web</Text>
      </View>
    </PaperProvider>
  );
};

const styles = StyleSheet.create({
  container: {
    backgroundColor: Colors.primary,
    height: 70,
    overflow: 'hidden',
    paddingHorizontal: 25,
    paddingVertical: 25,
    alignItems: 'center',
    flexDirection: 'row',
    flex: 1,
    width: '100%',
  },
  text: {
    color: Colors.black,
    letterSpacing: 0,
    lineHeight: 28,
    fontWeight: '700',
    textAlign: 'left',
    fontSize: 24,
  },
  icon: {
    color: Colors.black,
    paddingRight: 10,
    fontSize: 30,
  },
});
